import { TSelectOptions } from '@/components/Select';
import { TTransactionGeneralLedgerCode } from '@/context/types/Business';
import { NotificationType } from '@/context/types/Notification';
import useHomeContext from '@/hooks/useHomeContext';
import {
  removeTransactionGeneralLedgerCode,
  updateTransactionGeneralLedgerCode
} from '@/services/generalLedgerCodes';

const useGeneralLedgerCode = () => {
  const {
    setCurrentPageTransactions,
    setSelectedTransaction,
    transactionGeneralLedgerCodes,
    triggerNotification,
    setError
  } = useHomeContext()!;

  const updateTransactionDetails = (
    transactionId: string,
    newCode: TTransactionGeneralLedgerCode | undefined
  ) => {
    setSelectedTransaction((selected) =>
      selected
        ? {
            ...selected,
            details: {
              ...selected.details!,
              generalLedgerCode: newCode
            }
          }
        : selected
    );

    setCurrentPageTransactions((currentTxns) =>
      currentTxns.map((txn) =>
        txn.data.id === transactionId
          ? {
              ...txn,
              details: {
                ...txn.details!,
                generalLedgerCode: newCode
              }
            }
          : txn
      )
    );
  };

  const updateGeneralLedgerCodeForTransaction = async (
    option: TSelectOptions,
    transactionId: string,
    currentCodeId: string | null
  ) => {
    const newCodeId = option.value!;

    // Only update if there has been a change
    if (newCodeId !== currentCodeId) {
      await updateTransactionGeneralLedgerCode(transactionId, { generalLedgerCodeId: newCodeId })
        .then(() => {
          const newGeneralLedgerCode = transactionGeneralLedgerCodes?.find(
            (code) => code.id === newCodeId
          );

          updateTransactionDetails(transactionId, newGeneralLedgerCode);

          if (triggerNotification) {
            if (currentCodeId === null) {
              triggerNotification(NotificationType.TRANSACTION_GL_CODE_ADD_SUCCESS);
            } else {
              triggerNotification(NotificationType.TRANSACTION_GL_CODE_UPDATE_SUCCESS);
            }
          }
        })
        .catch(() => {
          if (setError) {
            setError(
              'An error has occurred updating the GL code for this transaction. Please contact support if the issue persists.'
            );
          }
        });
    }
  };

  const removeGeneralLedgerCodeForTransaction = async (transactionId: string) => {
    await removeTransactionGeneralLedgerCode(transactionId)
      .then(() => {
        updateTransactionDetails(transactionId, undefined);

        if (triggerNotification) {
          triggerNotification(NotificationType.TRANSACTION_GL_CODE_REMOVE_SUCCESS);
        }
      })
      .catch(() => {
        if (setError) {
          setError(
            'An error has occurred removing the GL code for this transaction. Please contact support if the issue persists.'
          );
        }
      });
  };

  const handleOnOptionRemoved = async (transactionId: string) => {
    removeGeneralLedgerCodeForTransaction(transactionId);
  };

  const handleOnOptionSelected = async (
    option: TSelectOptions,
    transactionId: string,
    currentCodeId: string | null
  ) => {
    updateGeneralLedgerCodeForTransaction(option, transactionId, currentCodeId);
  };

  return { handleOnOptionRemoved, handleOnOptionSelected };
};

export default useGeneralLedgerCode;
